import React from "react";
import { DataTable } from "../ui/data-table";
import { Clock, Calendar, Shield, MapPin } from "lucide-react";

/**
 * A configurable table component for displaying integration lists.
 * Wraps the generic DataTable and provides specific cell renderers for integrations.
 *
 * @param {Object} props
 * @param {Array} props.data - Array of integration data objects
 * @param {Array} props.columns - Configuration array for columns
 * @param {Function} props.onRowClick - Callback when a row is clicked
 * @param {Boolean} props.isLoading - Whether data is loading
 */
const IntegrationTypeTable = ({
  data,
  columns: configColumns,
  onRowClick,
  isLoading,
}) => {
  // Helper function to get nested value from object using dot notation
  const getNestedValue = (obj, path) => {
    return path.split(".").reduce((current, key) => current?.[key], obj);
  };

  // Helper to resolve cell renderer based on config type
  const getCellRenderer = (colConfig) => {
    // If a custom React component or function is passed (though likely config is JSON)
    if (typeof colConfig.cell === "function") return colConfig.cell;

    switch (colConfig.cell) {
      case "text":
        return (value) => (
          <span className="text-sm text-gray-900">{value || "-"}</span>
        );

      case "truncated_text":
        return (value) => (
          <span
            className="text-sm text-gray-600 truncate block"
            title={value}
            style={{ maxWidth: colConfig.maxWidth || "200px" }}
          >
            {value || "-"}
          </span>
        );

      case "date":
        return (value) => {
          if (!value) return <span className="text-sm text-gray-400">-</span>;
          const date = new Date(value);
          return (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Calendar className="w-3 h-3 text-gray-400" />
              <span>{date.toLocaleDateString()}</span>
            </div>
          );
        };

      case "datetime":
        return (value) => {
          if (!value) return <span className="text-sm text-gray-400">-</span>;
          const date = new Date(value);
          return (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Clock className="w-3 h-3 text-gray-400" />
              <span>
                {date.toLocaleDateString()} {date.toLocaleTimeString()}
              </span>
            </div>
          );
        };

      case "status_badge":
      case "status":
        return (value, row) => {
          const statusMap = colConfig.statusMap || {
            active: {
              label: "Active",
              color: "green",
              bg: "bg-green-100",
              text: "text-green-800",
            },
            scanning: {
              label: "Scanning",
              color: "blue",
              bg: "bg-blue-100",
              text: "text-blue-800",
            },
            inactive: {
              label: "Inactive",
              color: "gray",
              bg: "bg-gray-100",
              text: "text-gray-800",
            },
            error: {
              label: "Error",
              color: "red",
              bg: "bg-red-100",
              text: "text-red-800",
            },
          };

          const statusKey = (value || "inactive").toLowerCase();
          const config = statusMap[statusKey] || statusMap.inactive;

          // Allow override of styles from config if provided, otherwise use defaults
          const bgClass = config.bg || `bg-${config.color || "gray"}-100`;
          const textClass = config.text || `text-${config.color || "gray"}-800`;

          return (
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bgClass} ${textClass}`}
            >
              {config.label || value}
            </span>
          );
        };

      case "findings_counts":
        return (_, row) => {
          const critical = row.critical || row.critical_findings || 0;
          const high = row.high || row.high_findings || 0;
          const medium = row.medium || row.medium_findings || 0;
          const low = row.low || row.low_findings || 0;
          const total =
            row.totalFindings || critical + high + medium + low || 0;

          if (total === 0) {
            return <span className="text-sm text-gray-500">No findings</span>;
          }

          return (
            <div className="flex items-center gap-2">
              {critical > 0 && (
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800"
                  title="Critical"
                >
                  C: {critical}
                </span>
              )}
              {high > 0 && (
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800"
                  title="High"
                >
                  H: {high}
                </span>
              )}
              {medium > 0 && (
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800"
                  title="Medium"
                >
                  M: {medium}
                </span>
              )}
              {low > 0 && (
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800"
                  title="Low"
                >
                  L: {low}
                </span>
              )}
            </div>
          );
        };

      case "number":
        return (value) => (
          <span className="text-sm text-gray-900 font-mono">
            {value !== undefined ? value : 0}
          </span>
        );

      case "severity_count":
        return (value, row) => {
          const count = value || 0;
          if (count === 0)
            return <span className="text-sm text-gray-300">-</span>;

          const colorMap = {
            critical: "text-red-700 bg-red-50",
            high: "text-orange-700 bg-orange-50",
            medium: "text-yellow-700 bg-yellow-50",
            low: "text-blue-700 bg-blue-50",
            info: "text-gray-700 bg-gray-50",
            informational: "text-gray-700 bg-gray-50",
          };

          const severity = colConfig.severity || "info";
          const classes = colorMap[severity.toLowerCase()] || colorMap.info;

          return (
            <span
              className={`inline-flex items-center justify-center px-2.5 py-0.5 rounded-md text-xs font-medium ${classes}`}
              style={{ minWidth: "32px" }}
            >
              {count}
            </span>
          );
        };

      case "custom":
        // Fallback for custom configured renders in the future?
        return (value) => <span>{String(value)}</span>;

      default:
        return (value) => (
          <span className="text-sm text-gray-900">{value}</span>
        );
    }
  };

  // Transform config columns to DataTable columns
  const tableColumns = configColumns.map((col) => ({
    accessorKey: col.key,
    header: col.label,
    cell: (value, row) => {
      const render = getCellRenderer(col);
      // DataTable passes simple property access as first arg, which fails for nested keys (e.g. "stats.users_count").
      // So we resolve the value manually using the row data (which is the second arg).
      const actualValue = getNestedValue(row, col.key);
      return render(actualValue, row);
    },
    // Optional width/style from config
    headerStyle: col.width ? { width: col.width } : undefined,
  }));

  if (isLoading) {
    // Ensure we render something indicating loading if needed,
    // but usually the page handles the main loading spinner.
    return null;
  }

  return (
    <DataTable
      data={data}
      columns={tableColumns}
      onRowClick={onRowClick}
      emptyMessage="No items found."
      className="bg-white border rounded-md"
    />
  );
};

export default IntegrationTypeTable;

